import React, { useMemo, useState } from 'react';

const SortIcon = ({ direction }) => (
  <svg className="w-3 h-3 inline-block ml-1" viewBox="0 0 20 20" fill="currentColor">
    {direction === 'asc' ? (
      <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
    ) : (
      <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z" clipRule="evenodd" />
    )}
  </svg>
);

export default function ShortTickers({ tickers, shortSymbols }) {
  const [sortDir, setSortDir] = useState('desc');

  const shortTickers = useMemo(() => {
    if (!shortSymbols || shortSymbols.size === 0) return [];

    const list = [];
    for (const sym of shortSymbols) {
      const live = tickers[sym];
      if (live) {
        list.push({ sym, price: live.price, changePercent: live.changePercent, hasData: true });
      } else {
        list.push({ sym, price: null, changePercent: null, hasData: false });
      }
    }

    list.sort((a, b) => {
      if (a.hasData && !b.hasData) return -1;
      if (!a.hasData && b.hasData) return 1;
      if (a.hasData && b.hasData) {
        return sortDir === 'asc'
          ? a.changePercent - b.changePercent
          : b.changePercent - a.changePercent;
      }
      return a.sym.localeCompare(b.sym);
    });

    return list;
  }, [tickers, shortSymbols, sortDir]);

  const liveCount = useMemo(() => {
    return shortTickers.filter((t) => t.hasData).length;
  }, [shortTickers]);

  const toggleSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));

  const openTradingView = (symbol) => {
    window.open(
      `https://www.tradingview.com/chart/?symbol=${symbol}&interval=1`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  if (shortSymbols.size === 0) {
    return (
      <div className="bg-gray-900 rounded-2xl overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-800">
          <h2 className="text-sm font-medium text-gray-300 uppercase tracking-wider">Short Tickers</h2>
        </div>
        <div className="px-4 sm:px-6 py-12 text-center">
          <p className="text-sm text-gray-500">Loading short tickers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 rounded-2xl overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-300 uppercase tracking-wider">Short Tickers</h2>
          <span className="text-xs text-gray-500">
            {liveCount} / {shortSymbols.size} tickers live
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
              <th className="px-4 sm:px-6 py-3">Ticker</th>
              <th className="px-4 sm:px-6 py-3 text-right">Price</th>
              <th
                className="px-4 sm:px-6 py-3 text-right cursor-pointer select-none hover:text-gray-300 transition-colors"
                onClick={toggleSort}
              >
                Change %<SortIcon direction={sortDir} />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {shortTickers.map((ticker) => {
              if (!ticker.hasData) {
                return (
                  <tr
                    key={ticker.sym}
                    onClick={() => openTradingView(ticker.sym)}
                    className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3">
                      <span className="text-sm font-semibold text-gray-200">{ticker.sym}</span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                    </td>
                  </tr>
                );
              }
              const isPositive = ticker.changePercent >= 0;
              return (
                <tr
                  key={ticker.sym}
                  onClick={() => openTradingView(ticker.sym)}
                  className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                >
                  <td className="px-4 sm:px-6 py-3">
                    <span className="text-sm font-semibold text-gray-200">{ticker.sym}</span>
                  </td>
                  <td className="px-4 sm:px-6 py-3 text-right">
                    <span className="text-sm text-gray-300 font-medium tabular-nums">
                      ${ticker.price.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-3 text-right">
                    <span className={`inline-flex items-center gap-1 text-sm font-semibold tabular-nums ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                      {isPositive ? (
                        <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z" clipRule="evenodd" />
                        </svg>
                      )}
                      {isPositive ? '+' : ''}{ticker.changePercent.toFixed(2)}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
